import { Controller } from "@hotwired/stimulus";
import { get, post, destroy } from '@rails/request.js'
import { getRelativePosition } from 'chart.js/helpers';

export default class extends Controller {
  static targets = ["container"];
  static values = { annotationsPath: String };

  connect() {
    this.fetchAnnotations().then(() => this.annotateCharts());
  }

  async fetchAnnotations() {
    const response = await get(this.annotationsPathValue);
    if (response.ok) {
      this.annotations = await response.json;
    }
  }

  annotateCharts() {
    for (const container of this.containerTargets) {
      this.attemptAnnotation(container.id);
    }
  }

  attemptAnnotation(containerId) {
    const chartId = `chart-${containerId}`;
    const chartElement = document.getElementById(chartId);
    if (!chartElement) return;

    let chart = Chartkick.charts[chartId];
    if (chart) {
      this.annotateChart(chart.getChartObject(), containerId);
    } else {
      const observer = new MutationObserver(() => {
        chart = Chartkick.charts[chartId];
        if (chart) {
          this.annotateChart(chart.getChartObject(), containerId);
          observer.disconnect();
        }
      });

      observer.observe(chartElement, { childList: true, subtree: true });
    }
  }

  annotateChart(chartObject, containerId) {
    if (!chartObject) return;

    this.setClickHandler(chartObject);
    chartObject.options.plugins.annotation = this.buildAnnotationPlugin(chartObject, containerId);
    chartObject.update();
  }

  setClickHandler(chartObject) {
    if (chartObject.options.onClick) return;

    // Replace Chartkick's callback
    chartObject.options.scales.x.ticks.callback = (value) => {
      return chartObject.scales.x.getLabelForValue(value);
    };

    chartObject.options.onClick = (event) => {
      const canvasPosition = getRelativePosition(event, chartObject);
      const pixelValue = chartObject.scales.x.getValueForPixel(canvasPosition.x);
      const date = chartObject.scales.x.getLabelForValue(pixelValue);

      if (this.isDeleting) {
        this.isDeleting = false;
      } else {
        const annotation = global.window.prompt(`What happened on ${date}?`)?.trim();
        if (annotation) this.createAnnotation(date, annotation);
      }
    };
  }

  buildAnnotationPlugin(chartObject, containerId) {
    const annotations = {}
    for (const annotation of this.annotations) {
      annotations[annotation.on] = {
        type: 'point',
        backgroundColor: '#fbeeee',
        borderColor: '#e33b65',
        borderWidth: 1,
        pointStyle: 'triangle',
        radius: 8,
        xValue: annotation.on,
        yValue: chartObject.scales.y.min,
        enter() {
          const strong = document.createElement('strong');
          strong.textContent = `${annotation.on}:`;
          const span = document.createElement('span');
          span.textContent = annotation.annotation;
          const annotationContainer = document.getElementById(`graph-annotation-${containerId}`);
          annotationContainer?.replaceChildren(strong, ' ', span);
          return true;
        },
        leave() {
          const annotationContainer = document.getElementById(`graph-annotation-${containerId}`);
          annotationContainer?.replaceChildren('');
          return true;
        },
        click: () => {
          this.isDeleting = true
          const confirm = global.window.confirm(`Do you want to remove the annotation on ${annotation.on}?\n\n${annotation.annotation}`);
          if (confirm) this.detroyAnnotation(annotation.on);
        }
      }
    };

    return {
      clip: false,
      common: {
        drawTime: 'afterDatasetsDraw'
      },
      annotations
    };
  }

  async createAnnotation(on, annotation) {
    const response = await post(this.annotationsPathValue.split('?')[0],
      { body: JSON.stringify({ occurred_on: on, annotation }) }
    )
    if (response.ok) {
      this.annotations.push({ on, annotation });
      this.annotateCharts();
    }
  }

  async detroyAnnotation(on) {
    const response = await destroy(this.annotationsPathValue.split('?')[0] + `/${on}`)
    if (response.ok) {
      this.annotations = this.annotations.filter(annotation => annotation.on !== on)
      this.annotateCharts();
    }
  }
}
